import dayjs from 'dayjs'

import type { DelayUnit, TriggerType } from './types/marketingFlow'

export const TRANSACTIONAL_DEFAULT_TRIGGERS: TriggerType[] = ['CHECKIN', 'SIGNUP']

export const isTransactionalDefaultTrigger = (triggerType: TriggerType): boolean =>
  TRANSACTIONAL_DEFAULT_TRIGGERS.includes(triggerType)

export const getDelayMinutesValueFromUnit = (amount: number, unit: DelayUnit) => {
  if (unit === 'DAYS') {
    return amount * 24 * 60
  } else if (unit === 'HOURS') {
    return amount * 60
  } else if (unit === 'MINUTES') {
    return amount
  }
}

export const getDelayMinutesDivValueFromUnit = (amount: number, unit: DelayUnit) => {
  if (unit === 'DAYS') {
    return amount / 24 / 60
  } else if (unit === 'HOURS') {
    return amount / 60
  } else if (unit === 'MINUTES') {
    return amount
  }
}

export const isNumber = (value: number | string | null) => {
  if (value === null || value === '') {
    return true
  }

  const regex = /^\d+$/
  return regex.test(value?.toString() ?? '')
}

export const dateFromTime = (date: Date, time: string, addDay: boolean = false) => {
  const baseDate = dayjs(date).format('YYYY-MM-DD')
  const dateTime = dayjs(`${baseDate}T${time}`)
  return addDay ? dateTime.add(1, 'day').toDate() : dateTime.toDate()
}

export const getTimeRangeDate = (date: Date, timeRange: string) => {
  const splitTimeRange = timeRange?.split(' - ')
  if (splitTimeRange?.length !== 2) {
    return {
      start: dayjs(date).startOf('day').toDate(),
      end: dayjs(date).endOf('day').toDate()
    }
  }

  const startTime = splitTimeRange[0]
  const endTime = splitTimeRange[1]

  const startDate = dateFromTime(date, startTime)
  const endDate = dateFromTime(date, endTime)
  const isStartBeforeEnd = dayjs(startDate).isBefore(endDate)

  return {
    start: startDate,
    end: isStartBeforeEnd ? endDate : dateFromTime(date, endTime, true)
  }
}

// Utility function to convert ISO weekday number to localized weekday name
export const getLocalizedWeekday = (isoWeekday: string, locale: string): string => {
  const weekdayNumber = parseInt(isoWeekday, 10)
  const date = new Date()
  // Set to the next occurrence of this weekday
  const currentDay = date.getDay()
  const daysUntilTarget = (weekdayNumber - currentDay + 7) % 7
  date.setDate(date.getDate() + daysUntilTarget)

  return date.toLocaleDateString(locale, { weekday: 'long' })
}

export const isSameDay = (date1: Date, date2: Date) => {
  return dayjs(date1).isSame(date2, 'day')
}

export type MarketingFlowAudienceBlockCheck = {
  is_legacy: boolean
  is_active: boolean
  is_draft: boolean
}

export const isMarketingFlowBlockingAudienceEdit = (flow: MarketingFlowAudienceBlockCheck): boolean => {
  if (flow.is_legacy) {
    return flow.is_active
  }

  return !flow.is_draft && flow.is_active
}

export const sortActionsByPriority = <T extends { actionType: string }>(actions: T[]): T[] => {
  return [...actions].sort((a, b) => {
    // SEND_SMS should always be first
    if (a.actionType === 'SEND_SMS') return -1
    if (b.actionType === 'SEND_SMS') return 1
    // SEND_PUSH should be second
    if (a.actionType === 'SEND_PUSH') return -1
    if (b.actionType === 'SEND_PUSH') return 1
    // SEND_EMAIL should be third
    if (a.actionType === 'SEND_EMAIL') return -1
    if (b.actionType === 'SEND_EMAIL') return 1
    // Keep original order for other actions
    return 0
  })
}
